import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridValidRowModel,
  GRID_ACTIONS_COLUMN_TYPE,
  GRID_TREE_DATA_GROUPING_FIELD,
} from '@mui/x-data-grid-pro';
import { useDataGridProps } from './hooks/useDataGridProps';
import { useTableActionsContext } from './hooks/useTableActionsContext';
import { useTableContext } from './hooks/useTableContext';
import { RowProps, ScrollToTopButton, FooterProps } from './TableElements/components';
import { NoRowsOrResultsOverlayState } from './TableElements/components/NoRowsOverlayState';
import { ComponentType, useCallback, useEffect } from 'react';
import { useHasScrollUp } from './hooks/useHasScrollUp';
import { DEFAULT_ROW_HEIGHT, SKELETON_ROWS_COUNT } from './constants';
import { GridSettingsStore } from './store';

export interface TableProps<R extends GridValidRowModel = any>
  extends Omit<DataGridProProps<R>, 'pinnedColumns' | 'columns'>, RowProps, FooterProps {
  readonly groupColumnName?: string;
  readonly pinnedColumns?: string[];
  readonly onResetFilters?: () => void;
  readonly NoRowsOverlay?: ComponentType;
  readonly isLoading?: boolean;
  readonly hideDefaultScrollToTop?: boolean;
  readonly onScrollStateChange?: (showScrollToTop: boolean, handleScrollToTop: () => void) => void;
  readonly store?: GridSettingsStore;
  readonly columns: readonly GridColDef<R>[];
}

export default function Table<R extends GridValidRowModel = any>({
  groupColumnName = '',
  pinnedColumns = [],
  isLoading = false,
  hideDefaultScrollToTop = false,
  onScrollStateChange,
  columns,
  rows,
  onSortModelChange,
  apiRef,
  onColumnWidthChange,
  onColumnOrderChange,
  onResetFilters,
  NoRowsOverlay,
  sortModel,
  onRowsScrollEnd,
  isHoverable,
  rowCount,
  ...props
}: TableProps<R>) {
  const { isMultiselect, selectionModel } = useTableContext();
  const { onSelectionModelChange } = useTableActionsContext();

  const NoRowsOrResultsOverlayComponent = useCallback(() => {
    if (NoRowsOverlay) {
      return <NoRowsOverlay />;
    }

    return <NoRowsOrResultsOverlayState onResetFilters={onResetFilters} />;
  }, [NoRowsOverlay, onResetFilters]);

  const gridProps = useDataGridProps(
    groupColumnName,
    isMultiselect,
    NoRowsOrResultsOverlayComponent,
    isHoverable,
    props,
  );

  const gridPinnedColumns = {
    left: [...(groupColumnName ? [groupColumnName] : []), ...pinnedColumns],
    right: [GRID_ACTIONS_COLUMN_TYPE, GRID_TREE_DATA_GROUPING_FIELD],
  };

  const { handleScrollToTop, showScrollToTop } = useHasScrollUp(apiRef);

  useEffect(() => {
    onScrollStateChange?.(showScrollToTop, handleScrollToTop);
  }, [showScrollToTop, onScrollStateChange]);

  return (
    <div className="relative h-full min-h-0 w-full">
      <DataGridPro<R>
        apiRef={apiRef}
        columns={columns as GridColDef<R>[]}
        loading={isLoading}
        pinnedColumns={gridPinnedColumns}
        rowCount={rowCount}
        rows={rows}
        rowSelectionModel={Array.from(selectionModel.keys())}
        scrollEndThreshold={DEFAULT_ROW_HEIGHT * SKELETON_ROWS_COUNT}
        sortModel={sortModel}
        onColumnOrderChange={onColumnOrderChange}
        onColumnWidthChange={onColumnWidthChange}
        onRowSelectionModelChange={onSelectionModelChange}
        onRowsScrollEnd={onRowsScrollEnd}
        onSortModelChange={onSortModelChange}
        {...gridProps}
      />
      {!hideDefaultScrollToTop && (
        <ScrollToTopButton
          className="absolute right-6 bottom-0.5"
          handleScrollToTop={handleScrollToTop}
          showScrollToTop={showScrollToTop}
        />
      )}
    </div>
  );
}
